import LocationOnIcon from '@material-ui/icons/LocationOn'
import RefreshIcon from '@material-ui/icons/Refresh'
import { ReactNode } from 'react'
import { useShowController } from 'react-admin'
import { Card, Divider, FlexBox } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Pill } from 'shared/components/Pill/Pill'
import { Text } from 'shared/components/Text/Text'
import {
  EquipmentStatus,
  EquipmentStatusLabel,
  OwnershipType,
  OwnershipTypeLabel,
} from 'shared/models/equipment'
import {
  blueGray,
  darkGray,
  lightGray1,
  lightGray2,
  lightgreye3,
  midGray,
  royalBlue,
} from 'shared/theme/colors'
import styled from 'styled-components'

import { UserLink } from '../User/UserLink'
import { TrailerIcon } from './TrailerIcon'

export function TrailerShow(props: any) {
  const { record } = useShowController(props)

  if (!record) {
    return null
  }

  // TODO: @public-equipment-management Update the fields when models are updated
  const status = EquipmentStatusLabel[record.status as EquipmentStatus]
  const airtableLink = record.airtable?.link ?? '#'
  const currentDriver = record.drivers?.[0]
  const currentLocation = record.current_location

  return (
    <FlexBox mt={4} mb={4} justifyContent="space-between" alignItems="flex-start" isColumn gap="20px">
      <FlexBox isColumn gap="4px" width="744px">
        <FlexBox alignItems="center" gap="12px">
          <TrailerIcon fontSize="large" />
          <Text variant="header2">
            Trailer — {record.source} {record.vehicle_number}
          </Text>
          <Pill background={status.background} color={status.color}>
            {status.label}
          </Pill>
        </FlexBox>

        <FlexBox>
          <Text variant="uppercaseLabel3" fontColor={midGray}>
            <span>{OwnershipTypeLabel[record?.ownership_type as OwnershipType]}</span>
            <span>&nbsp;&nbsp;•&nbsp;&nbsp;</span>
            <span>{record.type?.replace('_', ' ')}</span>
          </Text>
        </FlexBox>
      </FlexBox>

      <FlexBox gap="20px" isFullWidth width="744px">
        <FlexBox isColumn flex={1} gap="8px">
          <Text
            variant="header3"
            fontColor={darkGray}
            mb={2}
          >{`${record.vehicle_make} ${record.vehicle_model}`}</Text>
          <Text variant="label3" fontColor={midGray}>
            VIN# {`${record.vin_number}`}
          </Text>
          <Text variant="label3" fontColor={midGray}>
            Plate# {`${record.license_plate_number}-${record.license_plate_state}`}
          </Text>
        </FlexBox>
        <FlexBox isColumn flex={1} gap="8px">
          <FlexBox alignItems="center" mb={2} gap="8px">
            <LocationOnIcon fontSize="medium" htmlColor={blueGray} />
            {/* TODO: @public-equipment-management outbound location link */}
            <Text variant="header3" fontColor={royalBlue} mt={0.5} ml={0.5}>
              {`${currentLocation?.city ?? '-'}, ${currentLocation?.state ?? '-'}`}
            </Text>
            <Text variant="header5" fontColor={lightGray2} mt={0.5}>{`${currentLocation?.age ?? '-'}m`}</Text>
            {/* TODO: @public-equipment-management refresh function for location */}
            <RefreshIcon fontSize="small" htmlColor={royalBlue} />
          </FlexBox>
          <Text variant="label3" fontColor={lightGray1}>
            Current driver
          </Text>
          <FlexBox>
            <UserLink user={currentDriver}>
              <Text variant="header4" fontColor={royalBlue}>
                {currentDriver?.fullName ?? '-'}
              </Text>
            </UserLink>
            <Divider vertical margin="0 12px" />
            <Text variant="header4" fontColor={royalBlue}>
              {currentDriver?.team?.name ?? '-'}
            </Text>
          </FlexBox>
        </FlexBox>
      </FlexBox>

      <StyledCard noShadow>
        <FlexBox mb={2}>
          <Text variant="uppercaseLabel2" align="left">
            AIRTABLE
          </Text>
        </FlexBox>
        <FlexBox gap="16px">
          <List>
            <ListItem label="Location type" value={record.airtable?.locationType} />
            <ListItem label="Expected address" value={record.airtable?.expectedAddress} />
          </List>
          <List>
            <ListItem label="Condition" value={record.airtable?.condition} />
            <ListItem label="Utilized?" value={record.airtable?.utilized} />
          </List>
        </FlexBox>
        <Divider mt={2} mb={2} color={lightgreye3} />
        <FlexBox gap="16px">
          <List>
            <ListItem label="Uber freight?" value={record.airtable?.uberFreight ? 'Yes' : 'No'} />
            <ListItem label="Pickup in progress?" value={record.airtable?.pickupInProgress ? 'Yes' : 'No'} />
            <ListItem label="Drop in progress?" value={record.airtable?.dropInProgress ? 'Yes' : 'No'} />
            <ListItem label="Load recovery?" value={record.airtable?.loadRecovery ? 'Yes' : 'No'} />
            <ListItem label="Shipper freight?" value={record.airtable?.shipperFreight ? 'Yes' : 'No'} />
            <ListItem
              label="Termination in progress?"
              value={record.airtable?.terminationInProgress ? 'Yes' : 'No'}
            />
            <ListItem label="Potentially stolen?" value={record.airtable?.potentiallyStolen ? 'Yes' : 'No'} />
            <ListItem
              label="Pending legal action?"
              value={record.airtable?.pendingLegalAction ? 'Yes' : 'No'}
            />
            <ListItem label="Terminated?" value={record.airtable?.terminated ? 'Yes' : 'No'} />
            <ListItem
              label="Available for pickup?"
              value={record.airtable?.availableForPickup ? 'Yes' : 'No'}
            />
          </List>
          <List>
            <ListItem
              label="Driver payment to CT"
              value={record.airtable?.driverPaymentToCT ? 'Yes' : 'No'}
            />
            <ListItem label="Vendor monthly price" value={record.airtable?.vendorMonthlyPrice} />
            <ListItem label="Outbound location" value={record.airtable?.outboundLocation} />
            <ListItem label="Airtable" value={<ExternalLink href={airtableLink}>Link</ExternalLink>} />
          </List>
        </FlexBox>
      </StyledCard>

      <Divider width="100%" color={lightgreye3} />

      <FlexBox justifyContent="space-between" width="744px">
        <Button variant="primary" size="medium" href={airtableLink}>
          GO TO AIRTABLE
        </Button>
        <Button variant="text" size="medium">
          View documents
        </Button>
      </FlexBox>
    </FlexBox>
  )
}

const List = ({ children }: { children: ReactNode }) => (
  <FlexBox isColumn flex={1} gap="8px">
    {children}
  </FlexBox>
)

const ListItem = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <ListItemContainer>
    <Text variant="header4" fontColor={lightGray1} align="left">
      {label}
    </Text>
    <Text variant="label3" fontColor={darkGray} align="left">
      {value ?? '-'}
    </Text>
  </ListItemContainer>
)

const ListItemContainer = styled(FlexBox)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const StyledCard = styled(Card)`
  padding: 20px 24px;
  width: 100%;
  max-width: 744px;
`
